import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Switch,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CompareArrowsIcon from '@material-ui/icons/CompareArrows'
import EventNoteIcon from '@material-ui/icons/EventNote'
import { useHistory } from 'react-router-dom'

import { Routes } from '../../../navigation/config/routes'
import { useUser } from '../../../hooks/useUser'
import { useLeasingSeason } from '../hooks/useLeasingSeason'
import {
    useAllSeasonsDetails,
    useLeasingSeasonsList,
} from '../hooks/useLeasingSeasonQueries'
import {
    LeasingSeasonConfig,
    LeasingSeasonListItem,
} from '../types/leasingSeason.types'
import { getPriorSeasonPercentLeased } from '../utils/priorSeason'
import { displayProgressFromDetail } from '../utils/seasonProgressDisplay'
import { SeasonFormDialog } from './SeasonFormDialog'
import { SeasonListCard } from './SeasonListCard'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        backgroundColor: theme.palette.background.default,
    },
    pageHeader: {
        padding: theme.spacing(3, 3, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    titleBlock: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
    },
    titleIcon: {
        width: 40,
        height: 40,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main + '14',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    propertyLabel: {
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: theme.palette.primary.main,
    },
    subtitle: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
        maxWidth: 640,
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    content: {
        padding: theme.spacing(3),
        maxWidth: 880,
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    seasonList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    sectionLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
    },
    newSeasonCard: {
        minHeight: 120,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.5),
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(2.5, 3),
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        transition: 'border-color 0.2s ease, color 0.2s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
    },
    newSeasonIcon: {
        fontSize: 28,
        opacity: 0.6,
    },
}))

function listItemToConfig(item: LeasingSeasonListItem): LeasingSeasonConfig {
    return {
        name: item.name,
        startDate: item.start_date,
        endDate: item.end_date,
        goalPercent: item.goal_percent,
        leaseTermId: item.lease_term_id ?? null,
        renewalDeadline: item.renewal_deadline ?? null,
        renewalGoalPercent: item.renewal_goal_percent ?? null,
        allocations: {
            prospecting: item.allocation_prospecting,
            pipeline: item.allocation_pipeline,
            calls: item.allocation_calls,
            website: item.allocation_website,
        },
        leasingSeasonConfigId: item.leasing_season_config_id ?? null,
        leasingSeasonConfigName: item.leasing_season_config_name ?? null,
        leasingSeasonConfigLocked: item.leasing_season_config_locked ?? false,
        pipelineConfigId: item.pipeline_config_id ?? null,
        pipelineConfigName: item.pipeline_config_name ?? null,
        pipelineConfigLocked: item.pipeline_config_locked ?? false,
        applicationWorkbookId: item.application_workbook_id ?? null,
        applicationWorkbookName: item.application_workbook_name ?? null,
        applicationWorkbookLocked: item.application_workbook_locked ?? false,
    }
}

function sortByStartDateDesc(seasons: LeasingSeasonListItem[]) {
    return [...seasons].sort(
        (a, b) =>
            new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
    )
}

export const ManageSeasonsLanding: React.FC = () => {
    const classes = useStyles()
    const history = useHistory()
    const { workspaceUser } = useUser()
    const apartmentId =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'
            ? workspaceUser.active_workspace.id
            : undefined
    const propertyName =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'
            ? workspaceUser.active_workspace.name
            : undefined

    const leasingSeason = useLeasingSeason(apartmentId)
    const manageSeasonsQuery = useLeasingSeasonsList(apartmentId, {
        includeArchived: true,
    })

    const [formOpen, setFormOpen] = useState(false)
    const [formMode, setFormMode] = useState<'edit' | 'create'>('create')
    const [
        editingSeason,
        setEditingSeason,
    ] = useState<LeasingSeasonListItem | null>(null)
    const [showArchived, setShowArchived] = useState(false)
    const [
        archiveTarget,
        setArchiveTarget,
    ] = useState<LeasingSeasonListItem | null>(null)

    const editInitialConfig = useMemo(
        () => (editingSeason ? listItemToConfig(editingSeason) : null),
        [editingSeason],
    )

    const allSeasons = manageSeasonsQuery.data ?? []

    const activeSeasons = useMemo(
        () => sortByStartDateDesc(allSeasons.filter((s) => !s.is_archived)),
        [allSeasons],
    )

    const archivedSeasons = useMemo(
        () => sortByStartDateDesc(allSeasons.filter((s) => s.is_archived)),
        [allSeasons],
    )

    const visibleSeasons = useMemo(
        () =>
            showArchived
                ? [...activeSeasons, ...archivedSeasons]
                : activeSeasons,
        [activeSeasons, archivedSeasons, showArchived],
    )

    const openCreate = () => {
        setEditingSeason(null)
        setFormMode('create')
        setFormOpen(true)
    }

    const openEdit = (season: LeasingSeasonListItem) => {
        setEditingSeason(season)
        setFormMode('edit')
        setFormOpen(true)
    }

    const openSeasonWorkspace = (seasonId: number) => {
        history.push(Routes.manage.leasingSeason.root, { seasonId })
    }

    const handleCreate = async (
        config: LeasingSeasonConfig,
        makeCurrent: boolean,
        rollover?: { sourceSeasonId: number; leadIds: number[] },
    ) => {
        await leasingSeason.createSeasonWithConfig(
            config,
            makeCurrent,
            rollover,
            () => setFormOpen(false),
        )
    }

    const handleSaveEdit = (
        config: LeasingSeasonConfig,
        makeCurrent: boolean,
    ) => {
        if (!editingSeason) return
        leasingSeason.saveSeasonConfigForId(editingSeason.id, config, () => {
            if (makeCurrent && !editingSeason.is_current) {
                leasingSeason.setCurrentSeason(editingSeason.id)
            }
            setFormOpen(false)
            setEditingSeason(null)
        })
    }

    const seasonDetailQueries = useAllSeasonsDetails(
        visibleSeasons.map((season) => season.id),
    )

    const percentBySeasonId = useMemo(() => {
        const map = new Map<number, number>()
        visibleSeasons.forEach((season, index) => {
            const detail = seasonDetailQueries[index]?.data
            const metrics = displayProgressFromDetail(detail)
            if (metrics) {
                map.set(season.id, metrics.percentLeased)
            }
        })
        return map
    }, [seasonDetailQueries, visibleSeasons])

    const renderSeasonCard = (
        season: LeasingSeasonListItem,
        indexOffset: number,
    ) => {
        const index = indexOffset
        const detail = seasonDetailQueries[index]?.data
        const metrics = displayProgressFromDetail(detail)
        const queryLoading = seasonDetailQueries[index]?.isLoading

        return (
            <SeasonListCard
                key={season.id}
                season={season}
                percentLeased={metrics?.percentLeased}
                expectedPercent={metrics?.expectedPercent ?? null}
                lastYearPercent={getPriorSeasonPercentLeased(
                    activeSeasons,
                    season.id,
                    percentBySeasonId,
                )}
                isProgressLoading={queryLoading && !metrics}
                archiveActionsDisabled={leasingSeason.isArchivingSeason}
                onOpen={() => openSeasonWorkspace(season.id)}
                onEdit={() => openEdit(season)}
                onArchive={() => setArchiveTarget(season)}
                onUnarchive={() => leasingSeason.unarchiveSeason(season.id)}
            />
        )
    }

    const isListLoading =
        manageSeasonsQuery.isLoading || leasingSeason.isListLoading

    return (
        <Box className={classes.root}>
            <Box className={classes.pageHeader}>
                <Box className={classes.titleRow}>
                    <Box className={classes.titleBlock}>
                        <Box className={classes.titleIcon}>
                            <EventNoteIcon fontSize="small" />
                        </Box>
                        <Box>
                            {propertyName && (
                                <Typography className={classes.propertyLabel}>
                                    {propertyName}
                                </Typography>
                            )}
                            <Typography variant="h5">Manage Seasons</Typography>
                            <Typography className={classes.subtitle}>
                                Each season is a year-long campaign.
                            </Typography>
                        </Box>
                    </Box>
                    <Box className={classes.headerActions}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    color="primary"
                                    checked={showArchived}
                                    onChange={(event) =>
                                        setShowArchived(event.target.checked)
                                    }
                                />
                            }
                            label="Show archived"
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CompareArrowsIcon />}
                            disabled
                        >
                            Compare seasons
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={openCreate}
                        >
                            New season
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Box className={classes.content}>
                {isListLoading ? (
                    <Box display="flex" justifyContent="center" py={8}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box className={classes.seasonList}>
                        {activeSeasons.map((season, index) =>
                            renderSeasonCard(season, index),
                        )}
                        <Box
                            className={classes.newSeasonCard}
                            onClick={openCreate}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                ) {
                                    openCreate()
                                }
                            }}
                        >
                            <AddIcon className={classes.newSeasonIcon} />
                            <Typography variant="body2">
                                Start a new season
                            </Typography>
                        </Box>

                        {showArchived && archivedSeasons.length > 0 && (
                            <>
                                <Typography className={classes.sectionLabel}>
                                    Archived
                                </Typography>
                                {archivedSeasons.map((season, index) =>
                                    renderSeasonCard(
                                        season,
                                        activeSeasons.length + index,
                                    ),
                                )}
                            </>
                        )}

                        {showArchived &&
                            archivedSeasons.length === 0 &&
                            activeSeasons.length > 0 && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    No archived seasons.
                                </Typography>
                            )}
                    </Box>
                )}
            </Box>

            <SeasonFormDialog
                open={formOpen}
                mode={formMode}
                apartmentId={apartmentId}
                seasons={leasingSeason.seasons}
                initialConfig={editInitialConfig}
                isCurrentSeason={Boolean(editingSeason?.is_current)}
                onClose={() => {
                    setFormOpen(false)
                    setEditingSeason(null)
                }}
                onSave={formMode === 'edit' ? handleSaveEdit : undefined}
                onCreate={formMode === 'create' ? handleCreate : undefined}
                onDemoFinished={(seasonId) => {
                    leasingSeason.setSelectedSeasonId(seasonId)
                }}
                isSaving={leasingSeason.isSaving}
            />

            <Dialog
                open={archiveTarget != null}
                onClose={() => setArchiveTarget(null)}
            >
                <DialogTitle>Archive season?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Hide &ldquo;{archiveTarget?.name}&rdquo; from Manage
                        Seasons? You can restore it later. Season data is kept.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setArchiveTarget(null)}
                        disabled={leasingSeason.isArchivingSeason}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={leasingSeason.isArchivingSeason}
                        onClick={() => {
                            if (!archiveTarget) return
                            leasingSeason.archiveSeason(archiveTarget.id)
                            setArchiveTarget(null)
                        }}
                    >
                        Archive
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
